import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  Tooltip,
} from '@mui/material';
import { FC, useEffect } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogActions } from 'src/components/molecules/dialog';
import { usePrompt } from 'src/hooks/use-prompt';
import { Can } from 'src/permissions/can';
import { UserRole } from 'src/types/enum/user-role';
import { z } from 'zod';
import { useUpdateAdminRole } from '../../../hooks/api/use-update-admin-role';

interface Props extends DialogActions {
  adminToken: string;
  adminRole: UserRole;
}

interface FormValues {
  role: UserRole;
}

const UpdateAdminRoleDialog: FC<Props> = ({
  open,
  onCancel,
  adminToken,
  adminRole,
}) => {
  const { t } = useTranslation();

  const validationSchema = z.object({
    role: z.nativeEnum(UserRole),
  });

  const defaultValues = {
    role: UserRole.OWNER,
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitSuccessful, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(validationSchema),
    mode: 'onBlur',
    defaultValues,
  });

  usePrompt(isDirty);

  const handleOnCancel = () => {
    onCancel?.();
    reset(defaultValues);
  };

  const { mutate: updateAdminRole, isPending: isLoadingUpdateAdmin } =
    useUpdateAdminRole(handleOnCancel);

  const onSubmit: SubmitHandler<FormValues> = data => {
    updateAdminRole({
      token: adminToken,
      role: data.role,
    });
  };

  useEffect(() => {
    if (adminRole) {
      reset({
        role: adminRole ?? UserRole.OWNER,
      });
    }
    if (isSubmitSuccessful) {
      reset(defaultValues);
    }
  }, [reset, isSubmitSuccessful, adminRole]);

  const roleOptions = [
    {
      label: t('common:roles.owner'),
      description: t('organizationDetails:admins.dialog.ownerDescription'),
      value: UserRole.OWNER,
    },
    {
      label: t('common:roles.admin'),
      description: t('organizationDetails:admins.dialog.adminDescription'),
      value: UserRole.ADMIN,
    },
    {
      label: t('common:roles.viewer'),
      description: t('organizationDetails:admins.dialog.viewerDescription'),
      value: UserRole.VIEWER,
    },
  ];

  return (
    <Dialog
      open={open}
      title={t('organizationDetails:admins.dialog.updateRole.title')}
      onCancel={handleOnCancel}
      onConfirm={handleSubmit(onSubmit)}
      confirmButtonText={t('common:button.edit')}
      cancelButtonText={t('common:button.cancel')}
      confirmButtonId="invite_administrator_confirm_button"
      isConfirmButtonSubmitting={isLoadingUpdateAdmin}>
      <Can I="updateRole" a="TenantAdministrators" passThrough>
        {allowed => (
          <Tooltip
            title={t('common:notSufficientPermissions')}
            disableHoverListener={allowed}>
            <span>
              <Controller
                name="role"
                render={({ field }) => (
                  <RadioGroup
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 3,
                    }}
                    defaultValue={UserRole.OWNER}
                    {...field}>
                    {roleOptions.map(option => (
                      <Box key={option.value}>
                        <FormControlLabel
                          label={option.label}
                          control={<Radio value={option.value} />}
                        />
                        <FormHelperText>{option.description}</FormHelperText>
                      </Box>
                    ))}
                    {/* <FormControlLabel
                      label={t('common:roles.owner')}
                      disabled={!allowed}
                      control={<Radio value={UserRole.OWNER} />}
                    />
                    <FormControlLabel
                      label={t('common:roles.admin')}
                      disabled={!allowed}
                      control={<Radio value={UserRole.ADMIN} />}
                    />
                    <FormControlLabel
                      label={t('common:roles.viewer')}
                      disabled={!allowed}
                      control={<Radio value={UserRole.VIEWER} />}
                    /> */}
                  </RadioGroup>
                )}
                control={control}
              />
            </span>
          </Tooltip>
        )}
      </Can>
    </Dialog>
  );
};

export default UpdateAdminRoleDialog;
